import { Add, Close, CloudUpload, Delete, Search } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname, parseError } from "../../../utils";

interface Unsubscribe {
  id: string;
  email: string;
  reason?: string;
  source?: string;
  created: string;
}

interface UnsubscribedDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  getToken: () => Promise<string>;
  onError?: (message: string) => void;
}

const UnsubscribedDialog: React.FC<UnsubscribedDialogProps> = ({
  open,
  onClose,
  companyId,
  getToken,
  onError,
}) => {
  const [loading, setLoading] = useState(false);
  const [unsubscribes, setUnsubscribes] = useState<Unsubscribe[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [searchQuery, setSearchQuery] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadUnsubscribes = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/unsubscribes`, {
        params: {
          companyId,
          limit: pageSize,
          offset: page * pageSize,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setUnsubscribes(response.data.unsubscribes || []);
      setTotal(response.data.pagination?.total || 0);
    } catch (error) {
      onError?.(parseError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadUnsubscribes();
    }
  }, [open, page, pageSize, companyId]);

  const handleRemove = async (id: string) => {
    try {
      const token = await getToken();
      await axios.delete(`${hostname}/unsubscribes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      loadUnsubscribes();
    } catch (error) {
      onError?.(parseError(error));
    }
  };

  const handleAdd = async () => {
    if (!addEmail.trim()) return;
    setAdding(true);
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/unsubscribes`,
        { email: addEmail.trim(), companyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAddEmail("");
      setPage(0);
      loadUnsubscribes();
    } catch (error) {
      onError?.(parseError(error));
    } finally {
      setAdding(false);
    }
  };

  const parseEmailsFromCsv = (content: string): string[] => {
    const emails = new Set<string>();
    const lines = content.split(/\r?\n/);
    for (const line of lines) {
      if (!line.trim()) continue;
      const cells = line.split(",");
      for (const cell of cells) {
        const value = cell.trim().replace(/^"|"$/g, "").trim();
        if (value && value.includes("@") && value.toLowerCase() !== "email") {
          emails.add(value);
        }
      }
    }
    return Array.from(emails);
  };

  const handleCsvSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;

    setImporting(true);
    setImportSummary(null);
    try {
      const content = await file.text();
      const emails = parseEmailsFromCsv(content);
      if (emails.length === 0) {
        onError?.("No valid email addresses found in CSV");
        return;
      }

      const token = await getToken();
      const response = await axios.post(
        `${hostname}/unsubscribes/bulk`,
        { emails, companyId, reason: "csv_import" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { added = 0, skipped = 0, failed = 0 } = response.data || {};
      setImportSummary(
        `Imported ${added} new, ${skipped} already unsubscribed, ${failed} failed`
      );
      setPage(0);
      loadUnsubscribes();
    } catch (error) {
      onError?.(parseError(error));
    } finally {
      setImporting(false);
    }
  };

  const filtered = searchQuery
    ? unsubscribes.filter((u) =>
        u.email.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : unsubscribes;

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Unsubscribed Emails</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mb: 2,
            alignItems: { xs: "stretch", sm: "center" },
          }}
        >
          <TextField
            placeholder="Search by email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ flexGrow: 1, maxWidth: 400 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            placeholder="Add email to unsubscribe list"
            value={addEmail}
            onChange={(e) => setAddEmail(e.target.value)}
            size="small"
            type="email"
            sx={{ flexGrow: 1, maxWidth: 400 }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAdd();
            }}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAdd}
            disabled={adding || !addEmail.trim()}
          >
            Add
          </Button>
          <input
            type="file"
            accept=".csv,text/csv"
            ref={fileInputRef}
            onChange={handleCsvSelect}
            style={{ display: "none" }}
          />
          <Tooltip title="Upload a CSV. Any email addresses found will be added to the unsubscribe list.">
            <span>
              <LoadingButton
                variant="outlined"
                startIcon={<CloudUpload />}
                loading={importing}
                onClick={() => fileInputRef.current?.click()}
              >
                Import CSV
              </LoadingButton>
            </span>
          </Tooltip>
        </Box>

        {importSummary && (
          <Alert
            severity="info"
            onClose={() => setImportSummary(null)}
            sx={{ mb: 2 }}
          >
            {importSummary}
          </Alert>
        )}

        <Paper variant="outlined">
          {loading ? <LinearProgress /> : <Box sx={{ height: 4 }} />}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Unsubscribed</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", py: 4 }}
                      >
                        No unsubscribed emails
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {filtered.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.reason || "-"}</TableCell>
                    <TableCell>{u.source || "-"}</TableCell>
                    <TableCell>
                      {u.created ? moment(u.created).format("lll") : "-"}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Resubscribe (remove from list)">
                        <IconButton
                          size="small"
                          onClick={() => handleRemove(u.id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default UnsubscribedDialog;
